import { useMemo } from 'react';
import {
  addDays,
  differenceInCalendarDays,
  eachDayOfInterval,
  format,
  startOfDay,
  startOfWeek,
} from 'date-fns';
import { CheckCircle2, Flame, Gift, Loader2, XCircle } from 'lucide-react';
import {
  computeStreak,
  type HanziDailyLog,
  type HanziWord,
} from '../../services/hanziHeroService';
import { getPinyinDisplay } from '../../utils/pinyinUtils';

const WEEKDAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'] as const;

/** Local calendar date → add days (avoids UTC date-only pitfalls). */
function addCalendarDays(isoYmd: string, days: number): string {
  const [y, m, d] = isoYmd.split('-').map(Number);
  const base = new Date(y, m - 1, d);
  return format(addDays(base, days), 'yyyy-MM-dd');
}

function parseLocalDay(isoYmd: string): Date {
  const [y, m, d] = isoYmd.split('-').map(Number);
  return startOfDay(new Date(y, m - 1, d));
}

/** Mon–Sun week that contains `todayIso`, then three full weeks (28 cells). Row 1 always includes today. */
function fourWeekGridFromToday(todayIso: string): Date[] {
  const today = parseLocalDay(todayIso);
  const weekStart = startOfWeek(today, { weekStartsOn: 1 });
  return eachDayOfInterval({ start: weekStart, end: addDays(weekStart, 27) });
}

/** Latest consecutive check-in day ending at today or yesterday (local), or null. */
function getActiveStreakBoundsLocal(
  practiced: Set<string>,
  todayIso: string
): { startIso: string; endIso: string } | null {
  const yest = addCalendarDays(todayIso, -1);
  let end: string | null = null;
  if (practiced.has(todayIso)) end = todayIso;
  else if (practiced.has(yest)) end = yest;
  else return null;
  let start = end;
  while (practiced.has(addCalendarDays(start, -1))) {
    start = addCalendarDays(start, -1);
  }
  return { startIso: start, endIso: end };
}

/** Day 1 = first check-in of current streak; wheel every `minStreak` days along that run (3rd, 6th, …). No streak → anchor today (fresh run). */
function getPrizeAnchorIso(practiced: Set<string>, todayIso: string): string {
  const b = getActiveStreakBoundsLocal(practiced, todayIso);
  return b?.startIso ?? todayIso;
}

function isPrizeMilestoneDay(
  iso: string,
  anchorIso: string,
  minStreak: number,
  todayIso: string
): boolean {
  const min = Math.max(1, minStreak);
  if (min <= 1) return iso === todayIso;
  if (iso < anchorIso) return false;
  const rel = differenceInCalendarDays(parseLocalDay(iso), parseLocalDay(anchorIso)) + 1;
  return rel >= min && rel % min === 0;
}

function nextPrizeIsoInGrid(
  cells: Date[],
  fromIsoInclusive: string,
  anchorIso: string,
  minStreak: number,
  todayIso: string
): string | null {
  for (const d of cells) {
    const iso = format(d, 'yyyy-MM-dd');
    if (iso < fromIsoInclusive) continue;
    if (isPrizeMilestoneDay(iso, anchorIso, minStreak, todayIso)) return iso;
  }
  return null;
}

/** Streak as if today’s check-in were already logged (matches server after Record). */
function computeStreakAfterTodayCheckIn(logs: HanziDailyLog[], todayIso: string): number {
  const doneToday = logs.some((l) => l.calendarDate === todayIso && l.sessionCompleted);
  if (doneToday) return computeStreak(logs);
  const synthetic: HanziDailyLog = {
    id: '__today_pending__',
    tuteeId: '',
    studentName: '',
    calendarDate: todayIso,
    sessionCompleted: true,
    charactersPracticed: 0,
    createdAt: '',
    updatedAt: '',
  };
  return computeStreak([...logs, synthetic]);
}

function HanziStreakMiniCalendar({
  logs,
  todayIso,
  minStreak,
  streak,
  daysToGo,
  wheelHintLine,
}: {
  logs: HanziDailyLog[];
  todayIso: string;
  minStreak: number;
  streak: number;
  daysToGo: number;
  wheelHintLine: string;
}) {
  const practiced = useMemo(() => {
    const s = new Set<string>();
    for (const row of logs) {
      if (row.sessionCompleted) s.add(row.calendarDate);
    }
    return s;
  }, [logs]);

  const { gridCells, rangeLabel, prizeAnchorIso } = useMemo(() => {
    const cells = fourWeekGridFromToday(todayIso);
    const anchor = getPrizeAnchorIso(practiced, todayIso);
    const label = `${format(cells[0]!, 'MMM d')} — ${format(cells[cells.length - 1]!, 'MMM d, yyyy')}`;
    return { gridCells: cells, rangeLabel: label, prizeAnchorIso: anchor };
  }, [todayIso, practiced]);

  const toGoPhrase =
    daysToGo <= 0 ? 'Spin today!' : `${daysToGo} more day${daysToGo !== 1 ? 's' : ''} to go!`;

  return (
    <div className="overflow-visible rounded-2xl border border-slate-200/90 bg-gradient-to-b from-white to-slate-50/80 p-4 sm:p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Streak</p>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-3xl font-black tabular-nums text-slate-900">{streak}</span>
            <span className="text-sm font-medium text-slate-600">day{streak !== 1 ? 's' : ''}</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-900">
              <Flame className="h-3.5 w-3.5 text-amber-600" aria-hidden />
              current
            </span>
          </div>
        </div>
        <p className="max-w-[16rem] text-right text-xs leading-snug text-slate-600">
          Filled = checked in.{' '}
          <span className="inline-flex items-center gap-0.5 align-middle">
            <Gift className="h-3 w-3 text-amber-700" aria-hidden />
          </span>{' '}
          {wheelHintLine} {toGoPhrase}
        </p>
      </div>

      <div
        className="overflow-x-auto overflow-y-visible px-2 py-2 sm:px-3 sm:py-3"
        role="grid"
        aria-label="Four-week practice calendar from this week through three weeks ahead"
      >
        <div className="mx-auto w-full max-w-md flex flex-col gap-1.5">
          <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
            {WEEKDAY_LABELS.map((d, i) => (
              <div key={`${d}-${i}`} className="h-5 text-center">
                <span className="text-[10px] font-bold leading-5 text-slate-400">{d}</span>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1.5 sm:gap-2 [grid-template-columns:repeat(7,minmax(2.25rem,1fr))]">
            {gridCells.map((d) => {
              const iso = format(d, 'yyyy-MM-dd');
              const dayNum = format(d, 'd');
              const done = practiced.has(iso);
              const isToday = iso === todayIso;
              const isPrizeDay = isPrizeMilestoneDay(iso, prizeAnchorIso, minStreak, todayIso);
              const showGift = isPrizeDay && !done;
              const titleParts = [
                iso,
                done ? 'checked in' : null,
                isToday ? 'today' : null,
                isPrizeDay ? 'wheel / prize day' : null,
              ]
                .filter(Boolean)
                .join(' — ');

              return (
                <div
                  key={iso}
                  role="gridcell"
                  title={titleParts}
                  className={[
                    'relative box-border flex h-11 w-full min-h-[2.75rem] flex-col items-center justify-center rounded-md border text-center transition-colors',
                    done
                      ? 'border-amber-300/80 bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-sm'
                      : 'border-slate-200 bg-slate-100/80 text-slate-700',
                    isToday ? 'z-[1] shadow-[0_0_0_2px_rgba(245,158,11,0.95),0_0_0_4px_rgba(255,255,255,1)]' : '',
                  ].join(' ')}
                >
                  <span
                    className={[
                      'text-[11px] font-bold tabular-nums leading-none',
                      done ? 'text-white/95' : 'text-slate-700',
                    ].join(' ')}
                  >
                    {dayNum}
                  </span>
                  {showGift && (
                    <Gift
                      className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-800"
                      strokeWidth={2.25}
                      aria-hidden
                    />
                  )}
                  {isPrizeDay && done && (
                    <Gift
                      className="absolute right-0.5 top-0.5 h-3 w-3 text-white drop-shadow-sm"
                      strokeWidth={2.5}
                      aria-hidden
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <p className="mt-3 text-[11px] text-slate-400">{rangeLabel}</p>
    </div>
  );
}

function WordResultChip({ word, tone }: { word: HanziWord; tone: 'ok' | 'miss' }) {
  const py = getPinyinDisplay(word.character, word.pinyin);
  const isOk = tone === 'ok';
  return (
    <div
      className={[
        'flex items-center gap-3 rounded-xl border px-3 py-2.5 sm:px-4 sm:py-3',
        isOk
          ? 'border-emerald-200/90 bg-emerald-50/50'
          : 'border-rose-200/90 bg-rose-50/50',
      ].join(' ')}
    >
      <span
        className="shrink-0 text-2xl sm:text-3xl font-semibold text-slate-900 leading-none"
        style={{ fontFamily: 'KaTi Maobi, KaiTi, serif' }}
      >
        {word.character}
      </span>
      <div className="min-w-0 flex-1 border-l border-slate-200/80 pl-3">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Pinyin</p>
        <p className="truncate text-sm font-semibold text-slate-800 tabular-nums">{py || '—'}</p>
      </div>
      {isOk ? (
        <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" aria-hidden />
      ) : (
        <XCircle className="h-5 w-5 shrink-0 text-rose-500" aria-hidden />
      )}
    </div>
  );
}

export interface HanziSessionSummaryProps {
  sessionWords: HanziWord[];
  sessionOutcomeByCharacter: Record<string, boolean>;
  dailyLogs: HanziDailyLog[];
  logsLoading: boolean;
  gradientClass: string;
  prizeWheelMinStreak: number;
  isRecording: boolean;
  onRecord: () => void;
}

const HanziSessionSummary = ({
  sessionWords,
  sessionOutcomeByCharacter,
  dailyLogs,
  logsLoading,
  gradientClass,
  prizeWheelMinStreak,
  isRecording,
  onRecord,
}: HanziSessionSummaryProps) => {
  const { correctWords, missedWords } = useMemo(() => {
    const correct: HanziWord[] = [];
    const missed: HanziWord[] = [];
    for (const w of sessionWords) {
      const o = sessionOutcomeByCharacter[w.character];
      if (o === true) correct.push(w);
      else missed.push(w);
    }
    return { correctWords: correct, missedWords: missed };
  }, [sessionWords, sessionOutcomeByCharacter]);

  const total = sessionWords.length;
  const correctN = correctWords.length;
  const pct = total > 0 ? Math.round((correctN / total) * 100) : 0;

  const todayIso = format(new Date(), 'yyyy-MM-dd');
  const minStreak = Math.max(1, prizeWheelMinStreak);

  const streak = useMemo(() => computeStreak(dailyLogs), [dailyLogs]);

  const practicedSet = useMemo(() => {
    const s = new Set<string>();
    for (const row of dailyLogs) {
      if (row.sessionCompleted) s.add(row.calendarDate);
    }
    return s;
  }, [dailyLogs]);

  const gridCells = useMemo(() => fourWeekGridFromToday(todayIso), [todayIso]);
  const prizeAnchorIso = useMemo(() => getPrizeAnchorIso(practicedSet, todayIso), [practicedSet, todayIso]);
  const nextPrizeIso = useMemo(
    () => nextPrizeIsoInGrid(gridCells, todayIso, prizeAnchorIso, minStreak, todayIso),
    [gridCells, todayIso, prizeAnchorIso, minStreak]
  );
  const daysToGo = useMemo(() => {
    if (!nextPrizeIso) return 0;
    return Math.max(0, differenceInCalendarDays(parseLocalDay(nextPrizeIso), parseLocalDay(todayIso)));
  }, [nextPrizeIso, todayIso]);

  const wheelHintLine =
    minStreak <= 1
      ? 'marks the date you get to spin the wheel!'
      : `marks wheel days (every ${minStreak} days from the start of your streak).`;

  const willSpinAfterRecord = useMemo(
    () => computeStreakAfterTodayCheckIn(dailyLogs, todayIso) >= minStreak,
    [dailyLogs, todayIso, minStreak]
  );

  return (
    <div className="mx-auto w-full max-w-lg sm:max-w-xl">
      <div className="rounded-3xl border border-amber-200/60 bg-white/90 p-5 shadow-xl shadow-amber-900/5 backdrop-blur-sm sm:p-8">
        <div className="text-center">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-amber-700/90">Session summary</p>
          <h2 className="mt-2 text-2xl font-black tracking-tight text-slate-900 sm:text-3xl">打卡完成</h2>
          <p className="mt-2 text-sm text-slate-600">You practiced {total} character{total !== 1 ? 's' : ''} this round.</p>
        </div>

        <div className="mt-8 flex justify-center">
          <div
            className="relative flex h-36 w-36 items-center justify-center sm:h-40 sm:w-40"
            aria-label={`${correctN} of ${total} correct`}
          >
            <svg className="absolute inset-0 -rotate-90" viewBox="0 0 100 100" aria-hidden>
              <circle
                cx="50"
                cy="50"
                r="42"
                fill="none"
                stroke="currentColor"
                strokeWidth="10"
                className="text-slate-100"
              />
              <circle
                cx="50"
                cy="50"
                r="42"
                fill="none"
                stroke="currentColor"
                strokeWidth="10"
                strokeLinecap="round"
                strokeDasharray={`${(pct / 100) * 264} 264`}
                className="text-amber-500 transition-[stroke-dasharray] duration-500"
              />
            </svg>
            <div className="relative text-center">
              <p className="text-3xl font-black tabular-nums text-slate-900 sm:text-4xl">{pct}%</p>
              <p className="text-xs font-semibold text-slate-500">
                {correctN}/{total} recall
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8 grid gap-6 sm:grid-cols-2">
          <section aria-labelledby="hanzi-correct-heading">
            <h3
              id="hanzi-correct-heading"
              className="mb-3 flex items-center gap-2 text-sm font-bold text-emerald-800"
            >
              <CheckCircle2 className="h-4 w-4" aria-hidden />
              Correct ({correctWords.length})
            </h3>
            {correctWords.length === 0 ? (
              <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 px-3 py-6 text-center text-sm text-slate-500">
                None this round — keep going next time.
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {correctWords.map((w) => (
                  <li key={w.id}>
                    <WordResultChip word={w} tone="ok" />
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section aria-labelledby="hanzi-review-heading">
            <h3 id="hanzi-review-heading" className="mb-3 flex items-center gap-2 text-sm font-bold text-rose-800">
              <XCircle className="h-4 w-4" aria-hidden />
              Review ({missedWords.length})
            </h3>
            {missedWords.length === 0 ? (
              <p className="rounded-xl border border-dashed border-emerald-200/80 bg-emerald-50/40 px-3 py-6 text-center text-sm text-emerald-800/90">
                Perfect — every character recalled without extra writing practice.
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {missedWords.map((w) => (
                  <li key={w.id}>
                    <WordResultChip word={w} tone="miss" />
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <div className="mt-8">
          {logsLoading ? (
            <div className="flex h-32 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50/80">
              <Loader2 className="h-8 w-8 animate-spin text-amber-600" aria-hidden />
            </div>
          ) : (
            <HanziStreakMiniCalendar
              logs={dailyLogs}
              todayIso={todayIso}
              minStreak={minStreak}
              streak={streak}
              daysToGo={daysToGo}
              wheelHintLine={wheelHintLine}
            />
          )}
        </div>

        <button
          type="button"
          onClick={onRecord}
          disabled={isRecording}
          className={`mt-6 flex w-full items-center justify-center gap-2 rounded-2xl px-6 py-4 text-base font-bold text-white shadow-lg transition-opacity disabled:opacity-70 bg-gradient-to-r ${gradientClass}`}
        >
          {isRecording ? (
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
          ) : willSpinAfterRecord ? (
            <Gift className="h-5 w-5" aria-hidden />
          ) : (
            <Flame className="h-5 w-5" aria-hidden />
          )}
          {isRecording
            ? 'Saving…'
            : willSpinAfterRecord
              ? 'Spin the Wheel!'
              : 'Record 打卡'}
        </button>
      </div>
    </div>
  );
};

export default HanziSessionSummary;
